import React, {useEffect, useRef, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";

import {useAppSelector} from "../../hooks";
import * as Utils from "../../utils/Utils";
import {formatStringToLocalDateTime, getFromLocal} from "../../utils/Utils";
import * as LinkFactory from "../../routing/LinkFactory";
import clientAvatar from "../../assets/images/content/client-avatar.png";
import rcicAvatar from "../../assets/images/content/rcic-avatar.png";
import AxiosAgent from "../../api/AxiosAgent";
import api from "../../api/api";
import {UserTypes} from "../../constants/UserTypes";
import messageIcon from "../../assets/icons/message.svg";
import notificationIcon from "../../assets/icons/notification.svg";
import logo from "../../assets/images/blift_logo_v2.png";
import PoweredByImmimentor from "../../components/PoweredByImmimentor";
import CustomModal from "../../utils/CustomModal";
import {FaCog} from "react-icons/fa";
import BliftProBadge from "../../components/BliftProBadge";

const TopNav: React.FC<{showUserControls?: boolean}> = ({showUserControls = true}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [userType, setUserType] = useState("");
    const isLoggedIn = Utils.isTokenValid();
    const notifRef = useRef<HTMLLIElement>(null);
    const profileRef = useRef<HTMLDivElement>(null);
    const searchRef = useRef<HTMLDivElement>(null);
    const notificationCount = useAppSelector((state) => state.notificationCounter.notificationCount);
    const messageCount = useAppSelector((state) => state.messageCounter.messageCount);
    const [showNotifications, setShowNotifications] = useState(false);
    const notificationList = useAppSelector((state) => state.notificationList.notifications);
    const [profilePictureBase64, setProfilePictureBase64] = useState("");
    const [showProfileDropdown, setShowProfileDropdown] = useState(false);
    const [modalTitle, setModalTitle] = useState('');
    const [modalMessage, setModalMessage] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [, setSearchExpanded] = useState(true);
    const [searchQuery, setSearchQuery] = useState("");
    const [isTopNavLocked, setIsTopNavLocked] = useState(false);
    const searchBackState = location.state as { fromSearch?: boolean } | null;
    const showBackToSearchButton = location.pathname.startsWith(`${LinkFactory.rcic_consultation_booking}/`)
        && location.pathname !== LinkFactory.rcic_profile_update;
    const showPublicQaCtas = !isLoggedIn && location.pathname.startsWith(LinkFactory.expert_answers);

    useEffect(() => {
        const interval = setInterval(() => {
            const newCookieData = Utils.getUserType();
            if (newCookieData !== undefined && newCookieData !== userType) {
                setUserType(newCookieData);
                clearInterval(interval);
            }
            if (newCookieData === undefined) {
                setUserType("");
            }
        }, 500);

        return () => clearInterval(interval);
    }, [userType]);

    useEffect(() => {
        downloadProfilePicture();
        const handleClickOutside = (event: MouseEvent) => {
            if (notifRef.current && !notifRef.current.contains(event.target as Node)) {
                setShowNotifications(false);
            }
            if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
                setShowProfileDropdown(false);
            }
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setSearchExpanded(false);
            }
        };

        // Profile pages dispatch this after an upload so the avatar refreshes
        // without a re-login.
        const onProfilePictureUpdated = () => downloadProfilePicture();
        const onStorage = (e: StorageEvent) => {
            if (e.key === "PROFILE_PICTURE_URL") downloadProfilePicture();
        };

        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener("profile-picture-updated", onProfilePictureUpdated);
        window.addEventListener("storage", onStorage);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            window.removeEventListener("profile-picture-updated", onProfilePictureUpdated);
            window.removeEventListener("storage", onStorage);
        };
    }, []);

    useEffect(() => {
        if (userType === UserTypes.RCIC) {
            const compute = () => {
                const coreCompleted = getFromLocal("RCIC_CORE_COMPLETED") === 'true';
                const legalDone = getFromLocal("RCIC_LEGAL_SETUP_DONE") === 'true';
                setIsTopNavLocked(coreCompleted && !legalDone);
            };
            compute();
            // cross-tab changes
            const onStorage = (e: StorageEvent) => {
                if (e.key === "RCIC_CORE_COMPLETED" || e.key === "RCIC_LEGAL_SETUP_DONE") compute();
            };
            // same-tab changes dispatched by SideNavBar
            const onLockUpdate = () => compute();
            window.addEventListener("storage", onStorage);
            window.addEventListener("rcic-lock-status-updated", onLockUpdate);
            return () => {
                window.removeEventListener("storage", onStorage);
                window.removeEventListener("rcic-lock-status-updated", onLockUpdate);
            };
        } else {
            setIsTopNavLocked(false);
        }
    }, [userType]);

    const logout = () => {
        // Add your logout logic here
        Utils.logout();
        navigate(LinkFactory.login);

    };
    const canAccessFeatures = () => {
        return (userType === UserTypes.CLIENT || (userType === 'RCIC' && getFromLocal("RCIC_PROFILE_COMPLETION_STATUS") === 'true'))
    };

    const showLockedModal = () => {
        setModalTitle("Legal Agreements Required");
        setModalMessage("Please complete your Legal Agreements setup (consultation agreement, service agreement, and signature) before accessing this feature.");
        setShowModal(true);
    };

    const lockedIconStyle: React.CSSProperties = { pointerEvents: 'auto' };

    const allNotificationNavigation = () => {
        if (canAccessFeatures())
            navigate(LinkFactory.notification_list);
    };

    const navigateToMessages = () => {
        if (canAccessFeatures()) {
            navigate(LinkFactory.messages);
        }else{
            setModalTitle("Complete Onboarding");
            setModalMessage("Please complete platform onboarding to access this feature.");
            setShowModal(true);
        }
    };

    const toggleNotifications = () => {
        if (canAccessFeatures()) {
            setShowNotifications((prev) => !prev);
        }else{
            setModalTitle("Complete Onboarding");
            setModalMessage("Please complete platform onboarding to access this feature.");
            setShowModal(true);
        }
    };

    const handleSearchSubmit = (e?: React.FormEvent) => {
        e?.preventDefault();
        const q = searchQuery.trim();
        const destination = userType
            ? `${LinkFactory.rcic_search}${q ? `?q=${encodeURIComponent(q)}` : ''}`
            : `${LinkFactory.anonymous_rcic_search}${q ? `?q=${encodeURIComponent(q)}` : ''}`;
        setSearchExpanded(false);
        setSearchQuery('');
        navigate(destination);
    };

    const handleBackToSearchResults = () => {
        if (searchBackState?.fromSearch) {
            navigate(-1);
            return;
        }

        navigate(LinkFactory.rcic_search);
    };

    const redirectToProfileUpdate = () => {
        if (userType === UserTypes.CLIENT) navigate(LinkFactory.client_profile_update);
        else if (userType === 'RCIC')
            navigate(LinkFactory.rcic_profile_update);
    }

    const redirectToSettings = () => {
        if (userType === UserTypes.CLIENT) navigate(LinkFactory.settings_page_client);
        else if (userType === UserTypes.RCIC) navigate(LinkFactory.settings_page_rcic);
        else if (userType === UserTypes.ADMIN) navigate(LinkFactory.admin_settings);
    }

    const downloadProfilePicture = () => {
        // This nav also renders on public pages. The download needs a token, and a 401
        // here would send an anonymous visitor to the login page via the interceptor,
        // so a stale cached filename must not be enough to trigger it.
        if (!isLoggedIn) return;
        const profilePicture = getFromLocal("PROFILE_PICTURE_URL");
        if (profilePicture !== "null" && profilePicture != null && profilePicture.length > 0) {
            AxiosAgent.requests.post(api.USER + "/download-profile-picture", {'profilePictureName': profilePicture}).then((response) => {
                const data = response.data?.profilePictureData;
                if (!data) {
                    setProfilePictureBase64("");
                    return;
                }
                // The API returns either a full data URI or bare base64.
                setProfilePictureBase64(data.startsWith("data:") ? data : `data:image/jpeg;base64,${data}`);
            }).catch((error) => {
                console.error("Error downloading profile picture:", error);
            });
        } else {
            setProfilePictureBase64("");
        }

    }


    return (


        <nav className="top-nav">

            <CustomModal
                visible={showModal}
                title={modalTitle}
                message={modalMessage}
                onClose={() => {
                    setShowModal(false);
                }}
            ></CustomModal>


        <div className="top-bar">
            {/* Left — Logo */}
            <div className="topbar-left">
                <a href="https://www.blift.ca" title="Blift">
                    <img src={logo} alt="Blift Logo" title="Blift Logo" style={{ width: '80px', height: 'auto', cursor: 'pointer' }} />
                </a>
                <PoweredByImmimentor/>
            </div>

            {/* Center — RCIC Search (hidden for RCIC and ADMIN users) */}
            <div className="topbar-center">
                {showBackToSearchButton && (
                    <button
                        type="button"
                        className="topnav-back-search-btn"
                        onClick={handleBackToSearchResults}
                        aria-label="Back to search result"
                        title="Back to search result"
                        style={{ marginRight: "12px" }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Back to result
                    </button>
                )}
                {userType !== UserTypes.RCIC && userType !== UserTypes.ADMIN && (
                    <div className="topnav-search" ref={searchRef}>
                        <span className="topnav-search__prefix">
                            <svg width="18" height="20" viewBox="0 0 35 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_115_1456)">
                                    <path
                                        d="M7.5 10C7.5 12.6522 8.55357 15.1957 10.4289 17.0711C12.3043 18.9464 14.8478 20 17.5 20C20.1522 20 22.6957 18.9464 24.5711 17.0711C26.4464 15.1957 27.5 12.6522 27.5 10C27.5 7.34784 26.4464 4.8043 24.5711 2.92893C22.6957 1.05357 20.1522 0 17.5 0C14.8478 0 12.3043 1.05357 10.4289 2.92893C8.55357 4.8043 7.5 7.34784 7.5 10ZM14.8828 25.6406L16.3359 28.0625L13.7344 37.7422L10.9219 26.2656C10.7656 25.6328 10.1562 25.2188 9.52344 25.3828C4.05469 26.75 0 31.7031 0 37.6016C0 38.9297 1.07813 40 2.39844 40H32.6016C33.9297 40 35 38.9219 35 37.6016C35 31.7031 30.9453 26.75 25.4766 25.3828C24.8438 25.2266 24.2344 25.6406 24.0781 26.2656L21.2656 37.7422L18.6641 28.0625L20.1172 25.6406C20.6172 24.8047 20.0156 23.75 19.0469 23.75H15.9609C14.9922 23.75 14.3906 24.8125 14.8906 25.6406H14.8828Z"
                                        fill="currentColor"/>
                                </g>
                                <defs>
                                    <clipPath id="clip0_115_1456">
                                        <rect width="35" height="40" fill="white"/>
                                    </clipPath>
                                </defs>
                            </svg>
                        </span>
                        <form className="topnav-search__form" onSubmit={handleSearchSubmit}>
                            <span className="topnav-search__icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                                     fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                     strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                                </svg>
                            </span>
                            <input
                                type="text"
                                className="topnav-search__input"
                                placeholder="Search RCIC..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            <button type="submit" className="topnav-search__btn" aria-label="Search">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24"
                                     fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"
                                     strokeLinejoin="round">
                                    <line x1="5" y1="12" x2="19" y2="12"/>
                                    <polyline points="12 5 19 12 12 19"/>
                                </svg>
                            </button>
                        </form>
                    </div>
                )}
            </div>

            {/* Right — Notifications & Profile */}
            {showUserControls && (isLoggedIn || showPublicQaCtas) && (
            <div className="topbar-right">
                {!isLoggedIn && showPublicQaCtas && (
                    <div style={{display: "flex", alignItems: "center", gap: 8}}>
                        <button
                            type="button"
                            onClick={() => navigate(LinkFactory.login)}
                            className="btn"
                            style={{
                                padding: '0.5rem 1.25rem',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                color: '#111827',
                                background: 'transparent',
                                border: 'none'
                            }}
                        >
                            Sign In
                        </button>

                        <button
                            type="button"
                            onClick={() => navigate(LinkFactory.registration_type)}
                            className="btn"
                            style={{
                                padding: '0.625rem 1.5rem',
                                borderRadius: '9999px',
                                background: '#ef4444',
                                color: '#ffffff',
                                fontWeight: 600,
                                fontSize: '0.875rem',
                                border: 'none',
                                boxShadow: '0 6px 14px rgba(239,68,68,0.12)',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseEnter={(e) => ((e.currentTarget.style.background = '#dc2626'))}
                            onMouseLeave={(e) => ((e.currentTarget.style.background = '#ef4444'))}
                        >
                            Get started →
                        </button>
                    </div>
                )}

                {isLoggedIn && (
                    <>
                {/* Blift Pro Badge (for RCICs without Pro access) */}
                <BliftProBadge />

                {/* Messages */}
                {userType !== UserTypes.ADMIN && (
                    <div
                        className={`notification-wrapper${isTopNavLocked ? ' notification-wrapper--locked' : ''}`}
                        onClick={isTopNavLocked ? showLockedModal : navigateToMessages}
                        ref={notifRef as unknown as React.RefObject<HTMLDivElement>}
                        title={isTopNavLocked ? "Complete Legal Agreements setup to access" : undefined}
                    >
                        <button className="notification-button" onClick={isTopNavLocked ? showLockedModal : navigateToMessages}>
                            <img className="icon-btn" src={messageIcon} alt="Send Message" title="Send Message" style={{ width: "18px", height: "18px" }} />
                            {!isTopNavLocked && messageCount > 0 && (
                                <span className="notification-badge">{messageCount}</span>
                            )}
                        </button>
                    </div>
                )}
                {userType !== UserTypes.ADMIN && (
                    <div
                        className={`notification-wrapper${isTopNavLocked ? ' notification-wrapper--locked' : ''}`}
                        ref={notifRef as unknown as React.RefObject<HTMLDivElement>}
                        title={isTopNavLocked ? "Complete Legal Agreements setup to access" : undefined}
                    >
                        <button
                            className="notification-button"
                            onClick={isTopNavLocked ? showLockedModal : toggleNotifications}
                        >
                            <img className="icon-btn" src={notificationIcon} alt="Notifications" title="Notifications" style={{ width: "20px", height: "20px" }} />
                            {!isTopNavLocked && notificationCount > 0 && (
                                <span className="notification-badge">{notificationCount}</span>
                            )}
                        </button>

                        {showNotifications && (
                            <div className="notification-dropdown">
                                <div className="notification-header">
                                    <h4>Notifications</h4>
                                </div>

                                <div className="notification-list">
                                    {notificationList.length > 0 ? (
                                        notificationList.map((notif: any, idx: number) => (
                                            <div key={idx} className="notification-item" onClick={() => setShowNotifications(false)}>
                                                {/* Sender avatar */}
                                                {notif.senderProfilePicture ? (
                                                    <img
                                                        className="notif-avatar notif-avatar--sm"
                                                        src={notif.senderProfilePicture}
                                                        alt={notif.senderName ?? 'Sender'}
                                                        title={notif.senderName ?? 'Sender'}
                                                    />
                                                ) : (
                                                    <div
                                                        className="notif-avatar notif-avatar--sm"
                                                        style={{ background: ['#4f6ef7','#e05c8a','#14b085','#f59e0b','#7c3aed','#0ea5e9','#ef4444','#10b981'][idx % 8], color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: 600, borderRadius: '50%', flexShrink: 0 }}
                                                        aria-hidden
                                                    >
                                                        {((notif.senderName ?? notif.title ?? '?').trim().split(/\s+/).filter(Boolean).slice(0,2).map((w: string) => w[0]).join('')).toUpperCase()}
                                                    </div>
                                                )}
                                                <div className="notification-content">
                                                    <div className="notification-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                        <div className="notification-text" style={{ flex: 1, paddingRight: '8px', cursor: 'pointer' }} onClick={() => {
                                                            setShowNotifications(false);
                                                            navigate(LinkFactory.notification_list);
                                                        }}>
                                                            <div className="notification-subtitle">{notif.title}</div>
                                                            <div className="notification-body">{(notif.body || '').length > 70 ? `${(notif.body || '').slice(0, 70)}...` : (notif.body || '')}</div>
                                                        </div>
                                                        <span className="notification-time" style={{ whiteSpace: 'nowrap' }}>{(() => {
                                                            const d = new Date(notif.timestamp);
                                                            return isNaN(d.getTime())
                                                                ? formatStringToLocalDateTime(notif.timestamp)
                                                                : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
                                                        })()}</span>
                                                    </div>
                                                </div>
                                                {notif.unread && <span className="notification-dot"></span>}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="empty-notifications">No notification</div>
                                    )}
                                </div>

                                {notificationList.length > 0 && (
                                    <div className="notification-footer">
                                        <p style={{ fontSize: '14px', fontWeight: '500' }} onClick={() => allNotificationNavigation()}>View all notifications</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Profile Dropdown */}
                <div className="profile-dropdown" ref={profileRef}>
                    <button
                        className="profile-button"
                        onClick={() => setShowProfileDropdown(!showProfileDropdown)}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <img
                                src={profilePictureBase64 || (userType === UserTypes.RCIC ? rcicAvatar : clientAvatar)}
                                alt="Profile Picture"
                                title="Profile Picture"
                                className="profile-avatar"
                            />
                            <span className="profile-name">{getFromLocal("FIRST_NAME")} {getFromLocal("LAST_NAME")}</span>
                        </div>

                        <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 1.5L6 6.5L11 1.5" stroke="#8A94A6" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>

                    </button>

                    {showProfileDropdown && (

                        <div className="profile-dropdown-menu">
                            <div className="dropdown-user-header">
                                {getFromLocal("LAST_LOGIN") && (() => {
                                    const d = new Date(getFromLocal("LAST_LOGIN")!);
                                    const formatted = isNaN(d.getTime()) ? null : d.toLocaleString(undefined, {
                                        day: 'numeric', month: 'short', year: 'numeric',
                                        hour: '2-digit', minute: '2-digit',
                                    });
                                    return formatted
                                        ? <span className="dropdown-last-login">Last Login: {formatted}</span>
                                        : null;
                                })()}
                            </div>
                            <div className="dropdown-divider"></div>
                            {userType !== UserTypes.ADMIN && (
                                <>
                                    <button className="dropdown-item" onClick={() => {
                                        setShowProfileDropdown(false);
                                        redirectToProfileUpdate();
                                    }}>
                                        <svg className="dropdown-item-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Edit Profile" role="img">
                                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                            <path d="M20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                        </svg>
                                        <span style={{ paddingLeft: "10px" }}>Edit Profile</span>
                                    </button>

                                    <button type="button" className="dropdown-item" onClick={() => {
                                        setShowProfileDropdown(false);
                                        navigate(LinkFactory.support_page);
                                    }}>
                                        <svg className="dropdown-item-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Support" role="img" focusable="false">
                                            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" fill="none" />
                                            <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.5" fill="none" />
                                            <path d="M16.5 7.5l3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                            <path d="M7.5 16.5l-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                        {/*<img src={supportIcon} title="Support" className={"dropdown-item-icon"} />*/}
                                        <span style={{ paddingLeft: "10px" }}>Support</span>
                                    </button>
                                </>
                            )}
                            {(userType === UserTypes.CLIENT || userType === UserTypes.RCIC || userType === UserTypes.ADMIN) && (
                                <button type="button" className="dropdown-item" onClick={() => {
                                    setShowProfileDropdown(false);
                                    redirectToSettings();
                                }}>
                                    <FaCog className="dropdown-item-icon" aria-hidden="true" />
                                    <span style={{ paddingLeft: "10px" }}>Settings</span>
                                </button>
                            )}
                            <div className="dropdown-divider"></div>
                            <button className="dropdown-item" onClick={logout}>

                               <svg className="dropdown-item-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="Sign Out" role="img" focusable="false">
                                  <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M20 12h-9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                  <path d="M17 9l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                                {/*<img src={signoutIcon} title="Sign Out" className={"dropdown-item-icon"} />*/}
                                <span style={{ paddingLeft: "10px" }}>Log Out </span>
                            </button>
                        </div>
                    )}
                </div>
                    </>
                )}
            </div>
            )}
        </div>
        </nav>
    );
};
export default TopNav;
